(ns quuppa.qsm.view
  "Global view elements, like top bar and sidebar."
  (:require [re-frame.core :as rf]
            ["react" :as react]
            ["react-dom" :as react-dom]
            [reagent.core :as r]
            [reitit.frontend.easy :as rfe]
            [quuppa.ui.util :as ui-util]
            [quuppa.ui.icons :refer [icon]]
            [quuppa.peweb.query :as q]
            [quuppa.qsm.events :as e]
            [quuppa.qsm.features :as features]
            [quuppa.qsm.signin.view :as signin]
            [quuppa.qsm.site.events]
            [quuppa.qsm.projects.events :as project-e]
            [quuppa.qsm.permissions :as permissions]))

(def MainContext (react/createContext {}))

(defn nav-item [& args]
  (let [[props children] (ui-util/parse-args args)]
    (into [:li.nav-item.mb-1 props]
          children)))

(defn nav-link [{:keys [label href route-name active? class attrs]
                 :as props}]
  (let [current-page (or @(rf/subscribe [:nav/match :data :app-nav-page])
                         @(rf/subscribe [:nav/match :data :name]))
        parameters @(rf/subscribe [:nav/match :parameters :path])
        active?      (if active?
                       (active? current-page parameters)
                       (= route-name current-page))]
    [:a.nav-link.btn.btn-dark.d-flex.align-items-center.text-white
     (merge {:href  (or href
                        (rfe/href route-name))
             :class [class
                     (when active?
                       "active")]}
            (when active?
              {:aria-current "page"})
            attrs)
     (when (:icon props)
       [icon (:icon props)
        {:class "mr-3 fs-5"}])
     label
     (when (:icon-right props)
       [:<>
        [:div.flex-grow-1]
        [:button.btn.btn-sm.text-white.py-0.border-0
         {:type "button"
          :on-click (:icon-right-action props)}
         [icon (:icon-right props)
          {:class "fs-5"}]]])]))

(defn navbar []
  (let [{:keys [navbar-content-ref]} (react/useContext MainContext)]
    [:div.navbar.p-0.flex-shrink-0.justify-content-start

     ;; Match 1px border to navbar height
     [:button.navbar-toggler.d-md-none
      {:type "button"
       :data-toggle "collapse"
       :data-target :nav-menu
       :aria-controls "nav-menu"
       :aria-expanded "false"
       :aria-label "Toggle navigation"
       :on-click (fn [_e]
                   (rf/dispatch [::e/sidebar-toggle]))}
      [:span.navbar-toggler-icon]]

     [:div.qsm-sidebar.qsm-navbar-sidebar.h-100.d-flex.align-items-center
      [:a.navbar-brand
       {:data-test-id "navbar-logo"
        :href "#/"}
       [icon :quuppa-horizontal
        {:viewBox "0 0 88 18"}]]]

     [:nav.navbar.navbar-nav.flex-row.flex-grow-1.border-bottom
      ;; This component is only used if we already has a session
      ;; and the user has logged in.
      [:div.container-fluid.justify-content-between
       ;; Trick: have empty div displayed on small screen, so that
       ;; .justify-content-between will push the 'navbar-user-control's to
       ;; the right-most edge. Then hide it when viewport is sm or bigger and the navbar-content-ref kicks in.
       [:div.d-sm-none]
       [:div.d-none.d-sm-flex.align-items-center
        ;; Expose ref to this element, so portal can be used to
        ;; render to this div from other view components.
        ;; The navbar-content is hidden when viewport size is below 'sm' breakpoint.
        {:ref navbar-content-ref}]
       [signin/navbar-user-controls]]]]))

;; TODO: Problematic on smaller screens
(defn navbar-content
  "Render some content into the main top navbar through a React Portal"
  [& args]
  (let [[_props children] (ui-util/parse-args args)]
    ;; Could also use useContext hook, but this allows
    ;; using this component as a class component.
    [:> (.-Consumer MainContext)
     (fn [{:keys [navbar-content-ref]}]
       (when (.-current navbar-content-ref)
         (react-dom/createPortal
           (r/as-element (into [:<>] children))
           (.-current navbar-content-ref))))]))

(defn main-sidebar []
  (let [manage-privilege? (permissions/has-manage-permission?)
        [projects-expanded set-projects-expanded] (react/useState false)
        projects @(q/subscribe project-e/projects-query {} [:result])
        projects (sort-by :name projects)
        is-qsm-owner? (permissions/is-qsm-owner?)]

    [:aside.qsm-sidebar
     {:class [(when @(rf/subscribe [::e/sidebar-open]) "show")]}
     [:nav#nav-menu
      [:ul.nav.flex-column

       (when is-qsm-owner?
         [nav-item
          [nav-link
           {:label      "Dashboard"
            :icon       :briefcase
            :route-name :dashboard}]])

       [nav-item
        [nav-link
         {:label "Projects"
          :icon :briefcase
          :route-name :projects
          :active? (fn [current-page _parameters]
                     (or (= :projects current-page)
                         (and (not projects-expanded)
                              (= :project current-page))))
          :icon-right (if projects-expanded
                        :chevron-up
                        :chevron-down)
          :icon-right-action (fn [e]
                               (.preventDefault e)
                               (set-projects-expanded not))
          :attrs {:data-test-id "projects-link"}}]]

       (when projects-expanded
         (for [project projects]
           [nav-item
            {:key (:id project)}
            [nav-link
             {:class "pl-5"
              :label (:name project)
              :active? (fn [current-page parameters]
                         (and (= :project current-page)
                              (= (str (:id project)) (:project-id parameters))))
              :href (rfe/href :project {:project-id (:id project)})}]]))

       (when manage-privilege?
         [nav-item
          [nav-link
           {:label      "Engines"
            :icon       :tools
            :route-name :qpes
            :attrs      {:data-test-id "engines-link"}}]])

       (when manage-privilege?
         [nav-item
          [nav-link
           {:label "Users"
            :icon :people
            :route-name :users}]])
       (when (and manage-privilege?
                  (features/enabled? :planner))
         [nav-item
          [nav-link
           {:label "Planner"
            :icon :briefcase
            :route-name :planner}]])]]]))

(defn root
  "The 'root' of the page, includes navbar and main-sidebar."
  [& args]
  (let [[props children] (ui-util/parse-args args)]
    [:div.d-flex.flex-column.flex-grow-1
     [:f> navbar props]
     (into [:div.d-flex.flex-grow-1.position-relative
            [:f> main-sidebar]]
           children)]))

;; For all views (as QPE views need this)
(def root-controller
  {:id            :qsm-root
   :start         (fn []
                    (rf/dispatch [::e/root-start]))
   :subscriptions (fn [_]
                    [;; Global notification:
                     [:qpe-boot/event-report]])})
